import React, { useState } from 'react';
import { TicketStatus, TicketPriority, TICKET_STATUS_CONFIG, TICKET_PRIORITY_CONFIG } from './types';
import { TicketConversation } from './TicketConversation';
import { TicketSidebar } from './TicketSidebar';
import { UserAvatar } from './TicketShared';
import { useTicketDetail } from '@/hooks/useTicketState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ArrowLeft, User, Info } from 'lucide-react';
import type { WorkspaceMemberWithProfile } from '@/lib/workspaceMembers';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface TicketDetailViewProps {
  ticketId: string;
  onBack: () => void;
  isTeamMember: boolean;
  members: WorkspaceMemberWithProfile[];
  onTicketUpdated?: () => void;
}

export const TicketDetailView: React.FC<TicketDetailViewProps> = ({
  ticketId, onBack, isTeamMember, members, onTicketUpdated,
}) => {
  const { ticket, replies, loading, submitting, addReply, updateReply, deleteReply, changeStatus, assignTicket, reload } = useTicketDetail(ticketId);
  const [showMobileDetails, setShowMobileDetails] = useState(false);

  const handleChangeStatus = async (status: TicketStatus) => {
    await changeStatus(status);
    onTicketUpdated?.();
  };

  const handleAssign = async (assignedTo: string | null) => {
    await assignTicket(assignedTo);
    onTicketUpdated?.();
  };

  const handleChangePriority = async (priority: TicketPriority) => {
    if (!ticket) return;
    const { ticketService } = await import('@/lib/ticketService');
    await ticketService.updateTicket(ticketId, { priority });
    ticketService.clearCache();
    reload();
    onTicketUpdated?.();
  };

  if (loading) return <LoadingSpinner className="h-[60vh]" />;

  if (!ticket) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] space-y-4">
        <p className="text-muted-foreground">Ticket not found.</p>
        <button onClick={onBack} className="text-sm text-workspace-accent hover:underline">← Back to tickets</button>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header Row */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Back to Tickets</span>
        </button>

        <div className="flex items-center justify-end gap-3 flex-wrap">
          {/* Details Toggle (Mobile Only) */}
          <button
            onClick={() => setShowMobileDetails(!showMobileDetails)}
            className="xl:hidden flex items-center justify-center w-8 h-8 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors outline-none focus:ring-0"
            title="Toggle Details"
          >
            <Info className="w-5 h-5" />
          </button>

          {/* Assigned To Dropdown */}
          {isTeamMember && (
            <Select value={ticket.assigned_to || 'none'} onValueChange={v => handleAssign(v === 'none' ? null : v)}>
              <SelectTrigger className="h-8 w-auto px-3 py-1.5 text-sm font-medium rounded-full border-0 bg-muted text-foreground hover:bg-muted/80 focus:ring-0 focus:ring-offset-0 outline-none shadow-none [&>svg]:w-4 [&>svg]:h-4">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent className="border-0 shadow-xl bg-popover/95 backdrop-blur-sm">
                <SelectItem value="none">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <User className="w-4 h-4" />
                    <span>Unassigned</span>
                  </div>
                </SelectItem>
                {members.map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    <div className="flex items-center gap-2">
                      <UserAvatar name={m.name || m.email} avatar={m.avatar} size="sm" />
                      <span>{m.name || m.email}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Priority Dropdown */}
          {!isTeamMember && (
            <Select value={ticket.priority} onValueChange={handleChangePriority}>
              <SelectTrigger className={cn("h-8 w-auto px-3 py-1.5 text-sm font-medium rounded-full border-0 focus:ring-0 focus:ring-offset-0 outline-none shadow-none gap-2 [&>svg]:w-4 [&>svg]:h-4", TICKET_PRIORITY_CONFIG[ticket.priority]?.color || 'bg-muted text-muted-foreground')}>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <span className={`w-2 h-2 rounded-full ${TICKET_PRIORITY_CONFIG[ticket.priority]?.dotColor || 'bg-muted-foreground'}`} />
                  {TICKET_PRIORITY_CONFIG[ticket.priority]?.label || ticket.priority}
                </div>
              </SelectTrigger>
              <SelectContent className="border-0 shadow-xl bg-popover/95 backdrop-blur-sm">
                {Object.entries(TICKET_PRIORITY_CONFIG).map(([val, cfg]) => (
                  <SelectItem key={val} value={val}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${cfg.dotColor}`} />
                      {cfg.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Status Dropdown */}
          <Select value={ticket.status} onValueChange={handleChangeStatus}>
            <SelectTrigger className={cn("h-8 w-auto px-3 py-1.5 text-sm font-medium rounded-full border-0 focus:ring-0 focus:ring-offset-0 outline-none shadow-none gap-2 [&>svg]:w-4 [&>svg]:h-4", TICKET_STATUS_CONFIG[ticket.status]?.color || 'bg-muted text-muted-foreground')}>
              <div className="flex items-center gap-2 whitespace-nowrap">
                <span className={`w-2 h-2 rounded-full ${TICKET_STATUS_CONFIG[ticket.status]?.dotColor || 'bg-muted-foreground'}`} />
                {TICKET_STATUS_CONFIG[ticket.status]?.label || ticket.status}
              </div>
            </SelectTrigger>
            <SelectContent className="border-0 shadow-xl bg-popover/95 backdrop-blur-sm">
              {Object.entries(TICKET_STATUS_CONFIG)
                .filter(([val]) => isTeamMember || !['in_progress', 'waiting_on_client'].includes(val) || val === ticket.status)
                .map(([val, cfg]) => (
                <SelectItem key={val} value={val}>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${cfg.dotColor}`} />
                    {val === 'open' && ticket.status === 'closed' ? 'Reopen' : cfg.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 grid-rows-[min-content_1fr] xl:grid-rows-1 gap-4 md:gap-6 h-[calc(100vh-200px)] md:h-[calc(100vh-180px)] xl:h-[calc(100vh-110px)]">
        {/* Main */}
        <div className="order-2 xl:order-1 xl:col-span-2 flex flex-col min-h-0">
          {/* Conversation */}
          <div className="bg-card border border-border rounded-xl p-6 flex flex-col flex-1 min-h-0">
            <div className="flex-1 min-h-0">
              <TicketConversation
                ticket={ticket}
                replies={replies}
                onAddReply={addReply}
                onUpdateReply={updateReply}
                onDeleteReply={deleteReply}
                onChangeStatus={handleChangeStatus}
                submitting={submitting}
                isTeamMember={isTeamMember}
                isClientMessage={(id) => {
                  if (!id) return false;
                  if (members && members.length > 0) {
                    return !members.some(m => m.id === id);
                  }
                  return id === ticket.created_by;
                }}
              />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className={cn(
          "order-1 xl:order-2 xl:col-span-1 h-full overflow-y-auto pr-1 scrollbar-thin",
          showMobileDetails ? "block" : "hidden xl:block"
        )}>
          <TicketSidebar
            ticket={ticket}
            replies={replies}
            isTeamMember={isTeamMember}
          />
        </div>
      </div>
    </div>
  );
};
